import styled from '@emotion/styled'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { CdsCard } from '..'
import Client from 'fhirclient/lib/Client'
import { fhirclient } from 'fhirclient/lib/types'
import { Dispatch, SetStateAction } from 'react'

const CriticalColour = '#D2222D'
const WarningColour = '#FFBF00'
const InfoColour = '#133B63'

const Content = styled.div`
	overflow-y: scroll;
	height: 100%;
	max-height: 47vh;
`

type CardParams = {
    indicator: string
    delay: number
}

const Card = styled.div<CardParams>`
    margin: 0.5rem;
    padding: 0.5rem;
    border-radius: 2px 2px 2px 2px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2), 0 3px 10px 0 rgba(0, 0, 0, 0.19);
    opacity: 0;
    
    animation-fill-mode: forwards;
    animation-name: start-animation;
    animation-duration: 500ms;

    ${props => {
        switch (props.indicator) {
            case 'critical':
                return `border: 1px solid ${CriticalColour};`
            case 'warning':
                return `border: 1px solid ${WarningColour};`
            case 'info':
                return `border: 1px solid ${InfoColour};`
            default:
                return `border: 1px solid ${InfoColour};`
        }
    }}

    ${props => {
        return `animation-delay: ${props.delay * 50}ms;`
    }}

    @keyframes start-animation
    {
        0% {
            opacity: 0;
            transform: scale3d(0.3, 0.3, 0.3);
        }
        
        50% {
            opacity: 1;
        }

        100% {
            opacity: 1;
        }
    }
`
type IndicatorDivParams = {
    indicator: string
}

const IndicatorDiv = styled.div<IndicatorDivParams>`
    margin: 0.5rem;
    font-weight: bold;
    font-size: 25px;
    ${props => {
        switch (props.indicator) {
            case 'critical':
                return `color: ${CriticalColour};`
            case 'warning':
                return `color: ${WarningColour};`
            case 'info':
                return `color: ${InfoColour};`
            default:
                return `color: ${InfoColour};`
        }
    }}
`

const Label = styled.div`
    margin: 0.5rem;
    font-weight: bold;
`

const Message = styled.div`
    display: block;
    margin-left: 0.5rem;
    margin-right: 0.5rem;
    margin-bottom: 0.5rem;
    p {
        margin-bottom: 0.5rem;
        margin-top: 0.5rem;
    }
    td {
        padding: 0.1rem;
    }
`

const Header = styled.div`
    display: flex;
    justify-content: left;
    align-items: center;
`

const Footer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
`

const ServiceUrl = styled.div`
    margin: 0.5rem;
    font-size: 8pt;
`

type CdsCardProps = {
    client: Client
    patient: fhirclient.FHIR.Patient
    cards: CdsCard[]
    setMessage: Dispatch<SetStateAction<string>>
    setDisplayMessageWindow: Dispatch<SetStateAction<boolean>>
}

export function CdsCards({ client, patient, cards, setMessage, setDisplayMessageWindow }: CdsCardProps) {

    function Indicator({ indicator }: { indicator: string }) {
        let indicatorText = ''

        switch (indicator) {
            case 'critical':
                indicatorText = 'Critical'
                break
            case 'warning':
                indicatorText = 'Warning'
                break
            case 'info':
                indicatorText = 'Information'
                break
        }

        return (
            <IndicatorDiv indicator={indicator}>{indicatorText}</IndicatorDiv>
        )
    }

    return <Content>
        {cards.map((card, index) => {
            return card ?
                <Card indicator={card.indicator} delay={index} key={index}>
                    <Header>
                        <Indicator indicator={card.indicator} />
                        <Label>{card.summary}</Label>
                    </Header>
                    <Message>
                        <ReactMarkdown remarkPlugins={[remarkGfm]} children={card.detail} />
                    </Message>
                    <Footer>
                        <ServiceUrl>
                            {card.serviceUrl}
                        </ServiceUrl>
                    </Footer>
                </Card> :
                null
        })}
    </Content>
}
